import { useState, useEffect, useRef, useCallback } from "react";
import type { TabConfig } from "../NavigationTabs";
import {
  SCROLL_LOCK_DURATION,
  SCROLL_THRESHOLD,
  MIN_VISIBILITY_THRESHOLD,
} from "../constants";

export interface UseNavigationScrollOptions {
  scrollContext?: "admin" | "non-admin";
  tabConfig: TabConfig[];
}

export interface UseNavigationScrollReturn {
  isScrolled: boolean;
  scrollToSection: (index: number) => void;
  selectedTab: number | null;
}

export const useNavigationScroll = ({
  tabConfig,
  scrollContext = "non-admin",
}: UseNavigationScrollOptions): UseNavigationScrollReturn => {
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sectionVisibilityRef = useRef<Map<number, number>>(new Map());
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const findScrollContainer = useCallback((): HTMLElement => {
    if (scrollContext === "admin") {
      const panel = document.getElementById("panel");
      if (panel) {
        return panel;
      }
    }
    return document.documentElement;
  }, [scrollContext]);

  const scrollToSection = useCallback(
    (index: number) => {
      const config = tabConfig[index];
      if (!config) return;

      const element = document.getElementById(config.sectionId);
      if (!element) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      isScrollingRef.current = true;
      setSelectedTab(index);

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, SCROLL_LOCK_DURATION);
    },
    [tabConfig],
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current || findScrollContainer();
    if (!container) return;

    const scrollY =
      container === document.documentElement
        ? window.scrollY || document.documentElement.scrollTop || 0
        : container.scrollTop || 0;

    setIsScrolled(scrollY > SCROLL_THRESHOLD);
  }, [findScrollContainer]);

  useEffect(() => {
    scrollContainerRef.current = findScrollContainer();
    const container = scrollContainerRef.current;

    handleScroll();

    if (scrollContext === "admin" && container !== document.documentElement) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
      document.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (scrollContext === "admin" && container !== document.documentElement) {
        container.removeEventListener("scroll", handleScroll);
      } else {
        window.removeEventListener("scroll", handleScroll);
        document.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollContext, findScrollContainer, handleScroll]);

  const updateActiveTab = useCallback(() => {
    if (isScrollingRef.current) return;

    let maxVisibility = 0;
    let activeIndex: number | null = null;

    for (const [index, visibility] of sectionVisibilityRef.current.entries()) {
      if (visibility > maxVisibility) {
        maxVisibility = visibility;
        activeIndex = index;
      }
    }

    if (maxVisibility > MIN_VISIBILITY_THRESHOLD) {
      setSelectedTab(activeIndex);
    } else {
      setSelectedTab(null);
    }
  }, []);

  const findSectionIndex = useCallback(
    (sectionId: string): number =>
      tabConfig.findIndex(config => config.sectionId === sectionId),
    [tabConfig],
  );

  const processIntersectionEntry = useCallback(
    (entry: IntersectionObserverEntry) => {
      const index = findSectionIndex(entry.target.id);
      if (index !== -1) {
        sectionVisibilityRef.current.set(index, entry.intersectionRatio);
      }
    },
    [findSectionIndex],
  );

  useEffect(() => {
    const container = scrollContainerRef.current || findScrollContainer();

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(processIntersectionEntry);
      updateActiveTab();
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root:
        scrollContext === "admin" && container !== document.documentElement
          ? container
          : null,
      rootMargin: "-10%",
      threshold: 0.1,
    });

    tabConfig.forEach(config => {
      const element = document.getElementById(config.sectionId);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [
    scrollContext,
    findScrollContainer,
    processIntersectionEntry,
    updateActiveTab,
    tabConfig,
  ]);

  return {
    selectedTab,
    isScrolled,
    scrollToSection,
  };
};
